/**
 * SEO Helper utilities for improving organic search performance
 */

// Generate optimized title tags
export const generateOptimizedTitle = (
  baseTitle: string,
  location: string = 'Houston',
  maxLength: number = 60
): string => {
  const suffix = ` | Luis ROC - ${location}`;
  const maxBaseLength = maxLength - suffix.length;
  
  const trimmedTitle = baseTitle.length > maxBaseLength 
    ? baseTitle.substring(0, maxBaseLength - 3) + '...'
    : baseTitle;
  
  return trimmedTitle + suffix;
};

// Generate meta description with optimal length
export const generateOptimizedDescription = (
  description: string,
  maxLength: number = 155
): string => {
  if (description.length <= maxLength) return description;
  
  // Find the last complete word within the limit
  const trimmed = description.substring(0, maxLength - 3);
  const lastSpace = trimmed.lastIndexOf(' ');
  
  return trimmed.substring(0, lastSpace) + '...';
};

// Extract keywords from text
export const extractKeywords = (text: string, count: number = 10): string[] => {
  // Remove common stop words in Spanish and English
  const stopWords = new Set([
    'el', 'la', 'de', 'que', 'y', 'a', 'en', 'un', 'ser', 'se', 'no', 'haber', 'por', 'con', 'su', 'para', 'como', 'estar', 'tener', 'le', 'lo', 'todo', 'pero', 'más', 'hacer', 'o', 'poder', 'decir', 'este', 'ir', 'otro', 'ese', 'la', 'si', 'me', 'ya', 'ver', 'porque', 'dar', 'cuando', 'él', 'muy', 'sin', 'vez', 'mucho', 'saber', 'qué', 'sobre', 'mi', 'alguno', 'mismo', 'yo', 'también', 'hasta', 'año', 'dos', 'querer', 'entre', 'así', 'primero', 'desde', 'grande', 'eso', 'ni', 'nos', 'llegar', 'pasar', 'tiempo', 'ella', 'sí', 'día', 'uno', 'bien', 'poco', 'deber', 'entonces', 'poner', 'cosa', 'tanto', 'hombre', 'parecer', 'nuestro', 'tan', 'donde', 'ahora', 'parte', 'después', 'vida', 'quedar', 'siempre', 'creer', 'hablar', 'llevar', 'dejar', 'nada', 'cada', 'seguir', 'menos', 'nuevo', 'encontrar', 'algo', 'solo', 'decir', 'pueden', 'casa', 'bajo', 'país', 'dejar', 'while', 'may', 'after', 'back', 'also', 'world', 'their', 'these', 'will', 'way', 'many', 'should', 'where', 'much', 'your', 'such', 'into', 'most', 'over', 'two', 'our', 'even', 'any', 'there', 'can', 'only', 'come', 'its', 'now', 'than', 'find', 'other', 'been', 'who', 'more', 'if', 'no', 'when', 'up', 'use', 'some', 'them', 'see', 'him', 'could', 'has', 'or', 'an', 'as', 'do', 'at', 'this', 'but', 'his', 'by', 'from', 'they', 'we', 'say', 'her', 'she', 'or', 'an', 'will', 'my', 'one', 'all', 'would', 'there', 'their', 'the', 'and', 'for', 'are', 'with', 'that', 'not', 'you', 'this', 'was', 'have', 'what', 'were', 'said', 'been', 'which', 'she', 'did', 'its'
  ]);

  const words = text.toLowerCase()
    .replace(/[^\w\sáéíóúñü]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 3 && !stopWords.has(word));

  // Count word frequency
  const frequency = words.reduce((acc, word) => {
    acc[word] = (acc[word] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Sort by frequency and return top N
  return Object.entries(frequency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, count)
    .map(([word]) => word);
};

// Generate slug from text
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

// Calculate reading time
export const calculateReadingTime = (text: string): number => {
  const wordsPerMinute = 200;
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};

// Generate breadcrumb items from URL
export const generateBreadcrumbs = (
  pathname: string,
  language: 'es' | 'en' = 'es'
): Array<{ name: string; url: string }> => {
  const parts = pathname.split('/').filter(Boolean);
  const breadcrumbs: Array<{ name: string; url: string }> = [];
  
  let currentPath = '';
  
  // Translation maps
  const translations: Record<string, Record<string, string>> = {
    es: {
      'services': 'Servicios',
      'servicios': 'Servicios',
      'blog': 'Blog',
      'about': 'Sobre Mí',
      'sobre-mi': 'Sobre Mí',
      'contact': 'Contacto',
      'contacto': 'Contacto',
      'tools': 'Herramientas',
      'herramientas-seo': 'Herramientas SEO',
      'faq': 'Preguntas Frecuentes',
      'case-studies': 'Casos de Éxito',
      'casos-de-exito': 'Casos de Éxito',
      'methodology': 'Metodología',
      'metodologia': 'Metodología'
    },
    en: {
      'services': 'Services',
      'servicios': 'Services',
      'blog': 'Blog',
      'about': 'About',
      'sobre-mi': 'About',
      'contact': 'Contact',
      'contacto': 'Contact',
      'tools': 'Tools',
      'herramientas-seo': 'SEO Tools',
      'faq': 'FAQ',
      'case-studies': 'Case Studies',
      'casos-de-exito': 'Case Studies',
      'methodology': 'Methodology',
      'metodologia': 'Methodology'
    }
  };
  
  parts.forEach(part => {
    currentPath += `/${part}`;
    const name = translations[language][part] || 
                 part.split('-').map(word => 
                   word.charAt(0).toUpperCase() + word.slice(1)
                 ).join(' ');
    
    breadcrumbs.push({ name, url: currentPath });
  });
  
  return breadcrumbs;
};

// Validate and sanitize URLs
export const sanitizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.toString();
  } catch {
    // If not a valid URL, assume it's a relative path
    return url.startsWith('/') ? url : `/${url}`;
  }
};

// Generate canonical URL
export const generateCanonicalUrl = (path: string): string => {
  const baseUrl = 'https://luisroc.com';
  const cleanPath = path.replace(/\/+$/, ''); // Remove trailing slashes
  return `${baseUrl}${cleanPath}`;
};

// Generate hreflang URLs
export const generateHreflangUrls = (path: string): {
  es: string;
  en: string;
  default: string;
} => {
  const baseUrl = 'https://luisroc.com';
  
  // Determine if path is Spanish or English
  const isEnglish = path.includes('/en/') || 
                    path.includes('/services/') || 
                    path.includes('/about') ||
                    path.includes('/en/contact') ||
                    path === '/en/contact';
  
  const spanishPath = path
    .replace('/en/', '/')
    .replace('/services/', '/servicios/')
    .replace('/about', '/sobre-mi')
    .replace('/en/contact', '/contacto')
    .replace('/case-studies', '/casos-de-exito')
    .replace('/methodology', '/metodologia');
  
  const englishPath = isEnglish ? path : 
    path
      .replace('/servicios/', '/services/')
      .replace('/sobre-mi', '/about')
      .replace('/contacto', '/en/contact')
      .replace('/casos-de-exito', '/case-studies')
      .replace('/metodologia', '/methodology');
  
  return {
    es: `${baseUrl}${spanishPath}`,
    en: `${baseUrl}${englishPath}`,
    default: `${baseUrl}${spanishPath}`
  };
};

// Check if URL is internal
export const isInternalUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url, 'https://luisroc.com');
    return urlObj.hostname === 'luisroc.com' || 
           urlObj.hostname === 'luisroc.com';
  } catch {
    return true; // Assume relative URLs are internal
  }
};



